<template>
  <div class="w-full max-w-[320px]">
    <div class="">
      <div>
        <ToggleGroupRoot v-model="selectedMode" type="single" class="flex">
          <ToggleGroupItem
            v-for="mode in searchModes"
            :key="mode.value"
            :value="mode.value"
            :aria-label="`Toggle ${mode.label} Search`"
            :class="[
              'text-mauve11 flex h-[44px] px-5 border-b border-b-transparent items-center justify-center bg-transparent text-base leading-4',
              'hover:bg-stone-50 dark:hover:bg-neutral-950 data-[state=on]:bg-stone-100 dark:data-[state=on]:bg-neutral-950 data-[state=on]:border-b-stone-100',
              'focus:z-10  focus:outline-none',
            ]"
          >
            {{ mode.label }}
          </ToggleGroupItem>
        </ToggleGroupRoot>
      </div>

      <!-- Search Combobox -->
      <div class="sixtyfourui-combobox relative">
        <ComboboxRoot v-model="selectedItem">
          <ComboboxAnchor>
            <ComboboxInput
              v-model="searchTerm"
              :displayValue="(val: any) => (val ? getDisplayValue(val) : '')"
              :placeholder="
                'Start typing to search ' +
                (selectedMode === 'players' ? 'players' : 'teams')
              "
              class="w-full p-3 border border-neutral-200 dark:border-neutral-700 rounded-lg focus:outline-none focus:border-cardinal dark:focus:border-cardinal-400"
            />
            <ComboboxTrigger
              class="absolute right-3 top-1/2 -translate-y-1/2 p-2"
            >
              <i class="fa-solid fa-search" />
            </ComboboxTrigger>
          </ComboboxAnchor>

          <ComboboxPortal>
            <ComboboxContent
              class="max-h-[40vh] overflow-hidden"
              position="popper"
              align="start"
            >
              <ComboboxViewport>
                <div
                  v-if="isLoading"
                  class="p-4 text-center text-neutral-600 dark:text-neutral-400"
                >
                  Loading...
                </div>
                <div
                  v-else-if="
                    !searchResults?.data?.length && searchTerm.length >= 3
                  "
                  class="p-4 text-center text-neutral-600 dark:text-neutral-400"
                >
                  No results found
                </div>
                <template v-else>
                  <ComboboxVirtualizer
                    v-slot="{ option }"
                    :options="searchResults?.data || []"
                    :text-content="getDisplayValue"
                    :estimate-size="41"
                  >
                    <ComboboxItem
                      :value="option"
                      class="flex items-center gap-3 p-2"
                    >
                      <template
                        v-if="selectedMode === 'teams' && option.logo_url"
                      >
                        <img
                          :src="option.logo_url"
                          :alt="option.team_name"
                          class="w-6 h-6 object-contain"
                        />
                      </template>
                      {{ getDisplayValue(option) }}
                    </ComboboxItem>
                  </ComboboxVirtualizer>
                </template>
              </ComboboxViewport>
            </ComboboxContent>
          </ComboboxPortal>
        </ComboboxRoot>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import {
  ComboboxAnchor,
  ComboboxTrigger,
  ComboboxCancel,
  ComboboxContent,
  ComboboxViewport,
  ComboboxInput,
  ComboboxItem,
  ComboboxPortal,
  ComboboxRoot,
  ComboboxVirtualizer,
  ToggleGroupItem,
  ToggleGroupRoot,
} from 'reka-ui';
import {
  useHomePageSearch,
  type SearchMode,
} from '~/composables/useHomePageSearch';

const searchModes = [
  { label: 'Players', value: 'players' as const },
  { label: 'Teams', value: 'teams' as const },
];

const selectedMode = ref<SearchMode>('players');
const searchTerm = ref('');
const selectedItem = ref<any>(null);
const toggleStateSingle = ref('left');
const toggleStateMultiple = ref(['italic']);

const { data: searchResults, isLoading } = useHomePageSearch(
  { searchTerm: toRef(searchTerm) },
  toRef(selectedMode)
);

const getDisplayValue = (item: any) => {
  if (!item) return '';
  return selectedMode.value === 'players' ? item.player_name : item.team_name;
};

watch(selectedMode, () => {
  console.log('selectedMode', selectedMode.value);
  searchTerm.value = '';
  selectedItem.value = null;
});

watch(selectedItem, (newValue) => {
  if (newValue) {
    console.log('newValue', newValue);
    const path =
      selectedMode.value === 'players'
        ? `/players/${newValue.player_id}`
        : `/teams/${newValue.team_id}`;
    navigateTo(path);
  }
});
</script>

<style lang="scss" scoped>
.sixtyfourui-combobox {
  @apply relative;
  input {
    @apply p-2 border border-neutral-200 [&:not(:focus)]:dark:border-neutral-400 rounded-md focus-visible:border-cardinal dark:focus-visible:border-cardinal-400 focus-visible:outline-none;
  }
}
// [id^='reka-combobox-content'] {
[data-reka-popper-content-wrapper] {
  // @apply absolute top-full left-0;
  @apply bg-white dark:bg-neutral-900 rounded-md p-2 border border-neutral-200 dark:border-neutral-800 w-[280px];
  z-index: 5 !important;
  [data-reka-collection-item] {
    @apply cursor-pointer px-1 py-2 flex [&:not(:last-child)]:border-b border-neutral-800 dark:hover:bg-white/[0.05] hover:bg-black/[0.05] w-full;
    &[data-highlighted] {
      @apply dark:bg-white/[0.05] bg-black/[0.05];
    }
  }
}
</style>
